import React, { useEffect, useRef, useState } from "react";
import { Share2, Copy, Check, X, Loader2, ExternalLink } from "lucide-react";

export default function ShareModal({
  open,
  onClose,
  onCreate,        // async () => {id, language, ...}
  initialShareUrl, // when opened for a just-created share
}) {
  const [creating, setCreating] = useState(false);
  const [shareUrl, setShareUrl] = useState(initialShareUrl || "");
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  // Auto-create on open
  useEffect(() => {
    if (!open) return;
    setShareUrl(initialShareUrl || "");
    setCopied(false);
    setErr("");
    if (!initialShareUrl) {
      (async () => {
        setCreating(true);
        try {
          const res = await onCreate?.();
          if (res?.url) setShareUrl(res.url);
          else setErr("Failed to create share link");
        } catch (e) {
          setErr(e?.message || "Failed to create share link");
        } finally {
          setCreating(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open && shareUrl && inputRef.current) {
      setTimeout(() => {
        try { inputRef.current.select(); } catch (_) { /* */ }
      }, 80);
    }
  }, [open, shareUrl]);

  async function copy() {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      // fallback
      try { inputRef.current?.select(); document.execCommand("copy"); setCopied(true); } catch (_) { /* */ }
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="share-modal"
    >
      <div
        className="bg-ide-panel border border-ide-borderHi rounded-xl shadow-glow w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-ide-border">
          <div className="flex items-center gap-2">
            <Share2 className="h-4 w-4 text-ide-accent" />
            <span className="text-sm font-semibold text-ide-text">Share your code</span>
          </div>
          <button
            onClick={onClose}
            className="text-ide-mute hover:text-ide-text p-1 rounded hover:bg-ide-surface"
            data-testid="share-close-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <p className="text-[12px] text-ide-mute">
            Anyone with this link can view and fork your code. No login required.
          </p>

          {creating && (
            <div className="flex items-center gap-2 text-ide-mute text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Generating link…
            </div>
          )}

          {err && (
            <div className="text-rose-300 text-sm border border-rose-500/30 bg-rose-500/10 rounded p-2">
              {err}
            </div>
          )}

          {shareUrl && (
            <>
              <div className="flex items-center gap-2 bg-ide-surface border border-ide-border rounded-md px-2 py-1.5">
                <input
                  ref={inputRef}
                  readOnly
                  value={shareUrl}
                  data-testid="share-url-input"
                  className="flex-1 bg-transparent text-sm font-mono text-ide-text truncate outline-none"
                />
                <button
                  onClick={copy}
                  className={
                    "flex-none flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors " +
                    (copied
                      ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                      : "bg-ide-accent/20 text-ide-accent border border-ide-accent/40 hover:bg-ide-accent/30")
                  }
                  data-testid="share-copy-btn"
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <a
                href={shareUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-ide-mute hover:text-ide-accent transition-colors"
              >
                <ExternalLink className="h-3 w-3" /> open in new tab
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
